import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, Renderer2 } from "@angular/core";
import { Subscription, debounceTime } from "rxjs";
import { TableSwitch } from "src/app/components/switch-table/switch-table.component";
import { FilterModelDto } from "src/app/models/filter.model";
import { SortDataColumn } from "src/app/models/table.model";
import { FilterSettingService } from "src/app/services/filter-setting.service";
import { ModalService } from "src/app/services/modal.service";
import { PermissionService } from "src/app/services/permission.service";
import { SortTableService } from "src/app/services/sort-table.service";
import { TableHeightService } from "src/app/services/table-height.service";
import { RecurrentPaymentInfoDto } from "./models/recurring-payment.models";
import { RecurringPaymentDataService } from "./services/recurring-payment-data.service";
import { FileNameSetService } from "src/app/services/file-name-set.service";
import { NotificationService } from "src/app/services/notification.service";
import { Router } from "@angular/router";

@Component({
	selector: "app-recurring-payment-list",
	templateUrl: "./recurring-payment-list.component.html",
	styleUrls: ["./recurring-payment-list.component.scss"],
})
export class RecurringPaymentListComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild("tableBlock") tableBlock: ElementRef | undefined;
	@ViewChild("tableBody") tableBody: ElementRef | undefined;

	delta = 150;
	lastPage!: boolean;
	currentPage = 0;
	sortColumnData: SortDataColumn | undefined;

	isOpenFilter = false;

	recurrentPaymentList: RecurrentPaymentInfoDto[] = [];

	subs: Subscription[] = [];

	filterAdditional: FilterModelDto[] | undefined;
	filterStatuses: FilterModelDto[] | undefined;
	filterCurrencies: FilterModelDto[] | undefined;
	filterRecPaymentPeriodTypes: FilterModelDto[] | undefined;
	filterNotifications: FilterModelDto[] | undefined;

	filterData: any;
	searchWord: string | undefined = "";
	fileName = "";

	constructor(
		private recurringDataService: RecurringPaymentDataService,
		private sortTableService: SortTableService,
		private tableHeightService: TableHeightService,
		private renderer: Renderer2,
		private filterSettingService: FilterSettingService,
		public permissionService: PermissionService,
		private fileNameSetService: FileNameSetService,
		private notificationService: NotificationService,
		private router: Router
	) {}

	ngOnInit() {
		this.initFilter();
		// this.loadRecurrentPaymentList()
		this.sortColumn("ID");
		this.fileNameInit();
	}

	ngAfterViewInit() {
		this.subs.push(
			this.tableHeightService.tableHeight$.subscribe({
				next: (res) => {
					this.renderer.setStyle(this.tableBlock?.nativeElement, "height", res);
				},
			})
		);
	}

	ngOnDestroy() {
		this.subs.forEach((item) => item.unsubscribe());
		this.sortTableService.sortColum$.next(undefined);
	}

	loadRecurrentPaymentList() {
		this.lastPage = true;
		this.recurringDataService.loadRecurringPaymentList(this.currentPage, this.searchWord, this.filterData).subscribe({
			next: (res) => {
				this.recurrentPaymentList.push(...res.content);
				this.currentPage++;
				this.lastPage = res.last;
			},
		});
	}

	fileNameInit() {
		this.subs.push(
			this.fileNameSetService.setFileName$.subscribe({
				next: (res) => {
					this.fileName = res;
				},
			})
		);
	}

	upload() {
		this.recurringDataService.loadFile().subscribe({
			next: (res) => {
				let link = document.createElement("a");
				link.href = (window.webkitURL || window.URL).createObjectURL(res);
				link.download = this.fileName;
				link.click();
			},
		});
	}

	initFilter() {
		this.subs.push(
			this.recurringDataService.loadRecurringPaymentFilter().subscribe({
				next: (res) => {
					this.filterStatuses = res["statuses"];
					this.filterCurrencies = res["currencies"];
					this.filterAdditional = res["additional_params"];
					this.filterRecPaymentPeriodTypes = res["recPaymentPeriodTypes"];
					this.filterNotifications = res["notifications"];
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterSetData$.subscribe({
				next: (res) => {
					this.filterData = res;
					this.currentPage = 0;

					this.recurrentPaymentList = [];
					this.loadRecurrentPaymentList();
				},
			})
		);

		this.subs.push(
			this.filterSettingService.inputSearch$.pipe(debounceTime(300)).subscribe({
				next: (res) => {
					this.recurringDataService.loadSearchList(res).subscribe({
						next: (res) => {
							this.filterSettingService.responseSearchData$.next(res.content);
						},
					});
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterSearchWord$.subscribe({
				next: (res) => {
					this.searchWord = res;
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterAllReset$.subscribe({
				next: () => {
					this.searchWord = "";
					this.filterData = undefined;
					this.currentPage = 0;

					this.recurrentPaymentList = [];
					this.loadRecurrentPaymentList();
				},
			})
		);
	}

	onScroll(event: any): void {
		const tableBodyHeight = this.tableBody?.nativeElement.clientHeight;
		const tableBlockHeight = this.tableBlock?.nativeElement.clientHeight;
		const scrollTable = event.target.scrollTop;
		if (tableBodyHeight - tableBlockHeight - scrollTable < this.delta && !this.lastPage) {
			this.loadRecurrentPaymentList();
		}
	}

	toggleActivateLink(item: TableSwitch) {
		this.recurringDataService.changeRecurrentPaymentStatus(item.id, !item.state).subscribe({
			next: () => {
				this.notificationService.setConfiguration(
					item.state ? "icon-2" : "icon-1",
					"warning",
					`RECURRENT_PAGE.NOTIFICATION.${item.state ? "RECURRENT_ON" : "RECURRENT_OFF"}`,
					"RECURRENT_PAGE.NOTIFICATION.DESCRIPTION"
				);
				this.notificationService.showNotification();
			},
		});
	}

	toggleFilter(filterState: boolean) {
		this.isOpenFilter = filterState;
	}

	sortColumn(column: string) {
		this.sortColumnData = this.sortTableService.sort(column);

		const sort = `order=${this.sortColumnData?.order}&field=${this.sortColumnData?.field}`;
		this.sortTableService.sortColum$.next(sort);

		this.currentPage = 0;
		this.recurrentPaymentList = [];
		this.loadRecurrentPaymentList();
	}

	navigateToPayments(item: RecurrentPaymentInfoDto) {
		this.router.navigate(["/payment-list"], { queryParams: { rec_payment_id: item.id } });
	}
}
